import {
  assert,
  assertType,
  range,
  Ref,
  RefMap,
  RefSet,
  notNull,
  getNumbering,
  TwinPair,
  swap,
  bothOrNull,
} from 'src/util';

import Game from './Game';
import Player from './Player';

import Calculator from './Calculator';
import App from './App';

type Room = {
  ref: Ref<Room>;
  name: string | null;

  entities: {
    players: RefSet<Player>; // TODO: Enforce names are unique
    games: RefSet<Game>;
  };

  paused: {
    players: RefMap<Player, boolean>,
    playerPairs: TwinPair<Ref<Player>>[],
  };

  gameList: Ref<Game>[];
};

function Room({ name = null, id = null }: { name?: string | null, id?: string | null } = {}): Room {
  return {
    ref: id === null ? Ref<Room>() : Ref.fromId<Room>(id),
    name,

    entities: {
      players: RefSet<Player>(),
      games: RefSet<Game>(),
    },

    paused: {
      players: RefMap<Player, boolean>(),
      playerPairs: [],
    },

    gameList: [],
  };
}

namespace Room {
  export type Action = (
    {
      type: 'set-name',
      data: string
    } |
    {
      type: 'add-player',
      data: Player,
    } |
    {
      type: 'add-game',
      data: Game,
    } |
    {
      type: 'remove-game',
      data: Ref<Game>,
    } |
    {
      type: 'clear-games',
    } |
    {
      type: 'set-player-paused',
      data: {
        player: Ref<Player>,
        paused: boolean,
      },
    } |
    {
      type: 'set-player-pair-paused',
      data: {
        playerPair: TwinPair<Ref<Player>>,
        paused: boolean,
      },
    } |
    never
  );

  export function reduce(room: Room, action: Action): Room {
    switch (action.type) {
      case 'set-name': {
        return { ...room,
          name: action.data,
        };
      }

      case 'add-player': {
        return { ...room,
          entities: { ...room.entities,
            players: RefSet.update(room.entities.players, action.data),
          },
        };
      }

      case 'add-game': {
        return { ...room,
          entities: { ...room.entities,
            games: RefSet.update(room.entities.games, action.data),
          },
          gameList: [...room.gameList, action.data.ref],
        };
      }

      case 'remove-game': {
        return { ...room,
          gameList: room.gameList.filter(game => game.id !== action.data.id),
        };
      }

      case 'clear-games': {
        return { ...room,
          entities: { ...room.entities,
            games: RefSet<Game>(),
          },
          gameList: [],
        };
      }

      case 'set-player-paused': {
        return { ...room,
          paused: { ...room.paused,
            players: RefMap.set(
              room.paused.players,
              action.data.player,
              action.data.paused,
            ),
          }
        };
      }

      case 'set-player-pair-paused': {
        return { ...room,
          paused: { ...room.paused,
            playerPairs: (() => {
              const sortedPair = (action.data.playerPair[0].id < action.data.playerPair[1].id
                ? action.data.playerPair
                : swap(action.data.playerPair)
              );

              const existingPair = room.paused.playerPairs.find(
                pair => pair[0].id === sortedPair[0].id && pair[1].id === sortedPair[1].id
              );

              if (Boolean(existingPair) === action.data.paused) {
                return room.paused.playerPairs;
              }

              return (existingPair
                ? room.paused.playerPairs.filter(pair => pair !== existingPair)
                : [...room.paused.playerPairs, sortedPair]
              );
            })(),
          },
        };
      }
    }
  }

  export function Players(room: Room): Player[] {
    return (RefSet
      .Refs(room.entities.players)
      .map(ref => RefSet.resolve(room.entities.players, ref))
      .filter(notNull)
    );
  }

  export function Games(room: Room): Game[] {
    return (room
      .gameList
      .map(ref => RefSet.resolve(room.entities.games, ref))
      .filter(notNull)
    );
  }

  export function WinMatrix(room: Room): number[][] {
    const players = getNumbering(room.entities.players);

    const winMatrix: number[][] = (range(players.size)
      .map(() => (range(players.size)
        .map(() => 0)
      ))
    );

    for (const game of Games(room)) {
      const i = RefMap.get(players.toNumber, game.players[0]);
      const j = RefMap.get(players.toNumber, game.players[1]);

      if (i === null || j === null) {
        assert(false);
        continue;
      }

      if (game.outcome === 'win') {
        winMatrix[i][j] += 1;
      } else if (game.outcome === 'loss') {
        winMatrix[j][i] += 1;
      } else {
        assertType<'draw'>(game.outcome);
        winMatrix[i][j] += 0.5;
        winMatrix[j][i] += 0.5;
      }
    }

    return winMatrix;
  }

  export function RatingsInput(room: Room): { W: number[][] } {
    return { W: WinMatrix(room) };
  }

  function X(room: Room, calculator: Calculator): number[] | null {
    return Calculator.get(calculator.Ratings, RatingsInput(room));
  }

  export function Ratings(
    room: Room,
    calculator: Calculator,
  ): RefMap<Player, number> | null {
    const x = X(room, calculator);

    if (x === null) {
      return null;
    }

    const players = getNumbering(room.entities.players);

    return players.fromNumber.reduce(
      (map, p, i) => RefMap.set(map, p, x[i]),
      RefMap<Player, number>(),
    );
  }

  export function ScaledRatingDiffs(
    room: Room,
    calculator: Calculator,
    players: TwinPair<Ref<Player>>,
  ): TwinPair<number> | null {
    const currentRatings = Ratings(room, calculator);

    if (!currentRatings) {
      return null;
    }

    const playerRatings = bothOrNull(TwinPair.map(players, p => RefMap.get(currentRatings, p)));

    if (playerRatings === null) {
      return null;
    }

    const newRatingsPair = bothOrNull(
      TwinPair.map(TwinPair<'win' | 'loss'>('win', 'loss'), outcome => Ratings(
        Room.reduce(room, {
          type: 'add-game',
          data: Game({ players, outcome }),
        }),
        calculator,
      ))
    );

    if (newRatingsPair === null) {
      return null;
    }

    const newPlayerRatingsPair = bothOrNull(TwinPair.fromIndexMapper(
      index => RefMap.get(newRatingsPair[index], players[index])
    ));

    if (newPlayerRatingsPair === null) {
      return null;
    }

    return TwinPair.fromIndexMapper(
      index => App.scaleRating(newPlayerRatingsPair[index]) - App.scaleRating(playerRatings[index])
    );
  }

  export function PairValuesInput(
    room: Room,
    calculator: Calculator,
  ): { W: number[][], x: number[] } | null {
    const x = X(room, calculator);

    if (x === null) {
      return null;
    }

    return { W: WinMatrix(room), x };
  }

  export function SuggestedGames(
    room: Room,
    calculator: Calculator,
  ): Game.Suggestion[] {
    const input = PairValuesInput(room, calculator);

    if (input === null) {
      return [];
    }

    const pairValues = Calculator.get(calculator.PairValues, input);

    if (pairValues === null) {
      // TODO: Indicate loading?
      return [];
    }

    const players = getNumbering(room.entities.players);

    return (pairValues
      .map(({ i, j, value }) => ({
        players: Player.Pair(players.fromNumber[i], players.fromNumber[j]),
        value,
      }))
      .filter(({ players }) => {
        if (
          RefMap.get(room.paused.players, players[0]) ||
          RefMap.get(room.paused.players, players[1])
        ) {
          return false;
        }

        const sortedPair = (players[0].id < players[1].id
          ? players
          : swap(players)
        );

        const pausedPairMatch = room.paused.playerPairs.find(
          pausedPair => (
            Ref.equals(pausedPair[0], sortedPair[0]) &&
            Ref.equals(pausedPair[1], sortedPair[1])
          )
        );

        if (pausedPairMatch) {
          return false;
        }

        return true;
      })
    );
  }
}

export default Room;



// WEBPACK FOOTER //
// ./src/state/Room.ts